import { verifyDevice } from '../services/auth.js';
import { ssoLogin, canUseSso } from '../services/sso.js';
import { clearAzureCredentials } from '../config.js';
import { t } from '../i18n.js';
import { createTwoFactorSetupScreen } from './two-factor-setup.js';
import { createTwoFactorVerifyScreen } from './two-factor-verify.js';

export function createLoginScreen(onLogin, onLoginSuccess) {
  const loginContainer = document.createElement('div');
  loginContainer.className = 'login-container';
  
  // Initially show without SSO, we'll check dynamically when user enters instance
  loginContainer.innerHTML = `
    <form class="login-form">
      <h2>${t('login_title')}</h2>

      <div class="login-value-proposition">
        <p>${t('login_description')}</p>
      </div>
      
      <div class="sso-section" style="display: none;">
        <button type="button" class="sso-button">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9.56" y="9.56" width="5.63" height="5.63" fill="#FEBA08"/>
            <rect x="2.81" y="9.56" width="5.63" height="5.63" fill="#05A6F0"/>
            <rect x="9.56" y="2.81" width="5.63" height="5.63" fill="#80BC06"/>
            <rect x="2.81" y="2.81" width="5.63" height="5.63" fill="#F25325"/>
          </svg>
          ${t('sso_button')}
        </button>
        <div class="divider">
          <span>${t('or')}</span>
        </div>
      </div>
      
      <div class="traditional-login">
        <div class="form-group">
          <label for="instance">${t('instance_label')}</label>
          <input type="text" id="instance" name="instance" placeholder="${t('instance_placeholder')}" required>
        </div>
        <div class="form-group">
          <label for="email">${t('email_label')}</label>
          <input type="email" id="email" name="email" required>
        </div>
        <div class="form-group">
          <label for="password">${t('password_label')}</label>
          <input type="password" id="password" name="password" required>
        </div>
        <button type="submit" class="login-button">${t('login_button')}</button>
      </div>

      <div class="login-footer">
        <p class="login-signup-text">
          ${t('login_new_user')}
          <a href="https://assist-go.de/en/demo-request/" target="_blank" rel="noopener noreferrer">${t('login_request_demo')}</a>
        </p>
        <p class="login-enterprise-text">
          ${t('login_enterprise_text')}
          <a href="mailto:support@proteam.de">support@proteam.de</a>
          ${t('login_enterprise_text2')}
        </p>
        <p class="login-enterprise-text">
          <a href="/assets/privacy.html" target="_blank" rel="noopener noreferrer">${t('privacy_policy')}</a>
        </p>
      </div>
      
      <div class="error-message" style="display: none;"></div>
    </form>
  `;

  const form = loginContainer.querySelector('form');
  const errorMessage = loginContainer.querySelector('.error-message');
  const ssoButton = loginContainer.querySelector('.sso-button');
  const ssoSection = loginContainer.querySelector('.sso-section');
  const instanceInput = form.querySelector('#instance');
  
  // Track current instance for SSO availability
  let currentInstance = null;
  let ssoCheckInProgress = false;

  // Function to check SSO availability for an instance
  async function checkSsoAvailability(instance) {
    if (!instance || ssoCheckInProgress || currentInstance === instance) {
      return;
    }
    
    ssoCheckInProgress = true;
    currentInstance = instance;
    
    try {
      const available = await canUseSso(instance);
      
      if (available) {
        ssoSection.style.display = 'block';
      } else {
        ssoSection.style.display = 'none';
      }
    } catch (error) {
      ssoSection.style.display = 'none';
    } finally {
      ssoCheckInProgress = false;
    }
  }
  

  // Listen to instance input changes to check SSO availability
  let instanceCheckTimeout;
  instanceInput.addEventListener('input', (e) => {
    const instance = e.target.value.trim();
    
    // Clear any existing timeout
    clearTimeout(instanceCheckTimeout);
    
    if (!instance) {
      ssoSection.style.display = 'none';
      currentInstance = null;
      clearAzureCredentials(); // Clear cached credentials when instance changes
      return;
    }
    
    // Debounce the SSO check to avoid too many API calls
    instanceCheckTimeout = setTimeout(() => {
      checkSsoAvailability(instance);
    }, 500);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const emailInput = form.querySelector('#email');
    const passwordInput = form.querySelector('#password');

    const instance = instanceInput.value.trim();
    const email = emailInput.value.trim();
    const password = passwordInput.value;

    if (!instance) {
      errorMessage.textContent = t('instance_required');
      errorMessage.style.display = 'block';
      return;
    }

    try {
      errorMessage.style.display = 'none';
      await onLogin(email, password, instance);
    } catch (error) {
      if (error.twoFactor) {
        showTwoFactorScreen(error.twoFactor, onLoginSuccess);
      } else if (error.requiresDeviceVerification) {
        showDeviceVerificationModal(error.email, error.deviceId, error.instance, onLoginSuccess);
      } else {
        errorMessage.textContent = error.message || t('login_failed');
        errorMessage.style.display = 'block';
      }
    }
  });

  // SSO button event handler
  if (ssoButton) {
    ssoButton.addEventListener('click', async (e) => {
      e.preventDefault();
      
      const instance = instanceInput.value.trim();
      
      if (!instance) {
        errorMessage.textContent = t('instance_required_sso');
        errorMessage.style.display = 'block';
        return;
      }
      
      let handledByTwoFactor = false;
      try {
        errorMessage.style.display = 'none';
        ssoButton.disabled = true;
        ssoButton.textContent = t('signing_in');
        
        const result = await ssoLogin(instance);
        await onLoginSuccess();
      } catch (error) {
        if (error.twoFactor) {
          handledByTwoFactor = true;
          showTwoFactorScreen(error.twoFactor, onLoginSuccess);
        } else {
          errorMessage.textContent = error.message || t('sso_login_failed');
          errorMessage.style.display = 'block';
        }
      } finally {
        if (!handledByTwoFactor) {
          ssoButton.disabled = false;
          ssoButton.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="9.56" y="9.56" width="5.63" height="5.63" fill="#FEBA08"/>
              <rect x="2.81" y="9.56" width="5.63" height="5.63" fill="#05A6F0"/>
              <rect x="9.56" y="2.81" width="5.63" height="5.63" fill="#80BC06"/>
              <rect x="2.81" y="2.81" width="5.63" height="5.63" fill="#F25325"/>
            </svg>
            ${t('sso_button')}
          `;
        }
      }
    });
  }

  // Function to show device verification modal (lazy loaded)
  async function showDeviceVerificationModal(email, deviceId, instance, onLoginSuccess) {
    
    try {
      // Dynamically import the modal component only when needed
      const { createDeviceVerificationModal } = await import('./device-verification.js');
      
      const modal = createDeviceVerificationModal(
        email,
        deviceId,
        async (email, deviceId, code) => {
          try {
            await verifyDevice(email, deviceId, code, instance);
            document.body.removeChild(modal);
            await onLoginSuccess();
          } catch (error) {
            if (error.twoFactor) {
              document.body.removeChild(modal);
              showTwoFactorScreen(error.twoFactor, onLoginSuccess);
              return;
            }
            throw error;
          }
        },
        () => {
          document.body.removeChild(modal);
        }
      );
      
      document.body.appendChild(modal);
    } catch (error) {
      // Fallback: show a simple alert
      alert(t('device_verification_support'));
    }
  }

  function showTwoFactorScreen(twoFactorData, onLoginSuccess) {
    const { requiresTwoFactorSetup, jwt: tfaToken, instance: tfaInstance } = twoFactorData;

    const parentContainer = loginContainer.parentElement;
    if (!parentContainer) return;

    parentContainer.innerHTML = '';

    const onTfaComplete = async () => {
      await onLoginSuccess();
    };

    const onTfaCancel = () => {
      parentContainer.innerHTML = '';
      parentContainer.appendChild(
        createLoginScreen(onLogin, onLoginSuccess)
      );
    };

    if (requiresTwoFactorSetup) {
      const setupScreen = createTwoFactorSetupScreen(tfaToken, tfaInstance, onTfaComplete, onTfaCancel);
      parentContainer.appendChild(setupScreen);
    } else {
      const verifyScreen = createTwoFactorVerifyScreen(tfaToken, tfaInstance, onTfaComplete, onTfaCancel);
      parentContainer.appendChild(verifyScreen);
    }
  }

  return loginContainer;
}
